import { LockfileManager } from "../LockfileManager.js";

export interface StatusResult {
    /** Whether replay is initialized (lockfile exists) */
    initialized: boolean;
    /** Total number of generations tracked */
    generationCount: number;
    /** Last generation info, if available */
    lastGeneration: StatusGeneration | undefined;
    /** Tracked customization patches */
    patches: StatusPatch[];
    /** Count of patches with "unresolved" or "resolving" status */
    unresolvedCount: number;
    /** Exclude patterns from replay.yml */
    excludePatterns: string[];
    /**
     * Customizations the customer permanently dismissed — via `forget`, or by
     * keeping the generator's version when resolving a conflict. These will
     * not be re-detected on future generations.
     */
    dismissed: StatusDismissal[];
    /**
     * Count of forgotten hashes recorded before dismissal metadata existed.
     * Such entries still tombstone re-detection but carry no message/commit
     * details to display.
     */
    legacyDismissedCount: number;
}

export interface StatusDismissal {
    /** The customer's original commit message */
    message: string;
    /** Short SHA of the original commit (7 chars; empty for synthetic patches) */
    sha: string;
    /** Which route dismissed it */
    via: "forget" | "resolve";
    /** ISO timestamp of the dismissal */
    dismissedAt: string;
}

export interface StatusPatch {
    /** Patch ID (e.g. "patch-def45678") */
    id: string;
    /** "added" if patch_content contains "new file mode", otherwise "modified" */
    type: "added" | "modified";
    /** Original commit message */
    message: string;
    /** Author name (without email) */
    author: string;
    /** Short SHA of the original commit (7 chars) */
    sha: string;
    /** Files touched by this patch */
    files: string[];
    /** Number of files */
    fileCount: number;
    /** Patch resolution status, if any */
    status?: "unresolved" | "resolving";
}

export interface StatusGeneration {
    /** Short commit SHA (7 chars) */
    sha: string;
    /** Generation timestamp */
    timestamp: string;
    /** CLI version used for generation */
    cliVersion: string;
    /** Generator versions (e.g. { "fern-java-sdk": "3.35.0" }) */
    generatorVersions: Record<string, string>;
}

export function status(outputDir: string): StatusResult {
    const lockManager = new LockfileManager(outputDir);

    if (!lockManager.exists()) {
        return {
            initialized: false,
            generationCount: 0,
            lastGeneration: undefined,
            patches: [],
            unresolvedCount: 0,
            excludePatterns: [],
            dismissed: [],
            legacyDismissedCount: 0,
        };
    }

    const lock = lockManager.read();

    const patches: StatusPatch[] = lock.patches.map((patch) => ({
        id: patch.id,
        type: patch.patch_content.includes("new file mode") ? "added" as const : "modified" as const,
        message: patch.original_message,
        author: patch.original_author.split("<")[0]?.trim() || "unknown",
        sha: patch.original_commit.slice(0, 7),
        files: patch.files,
        fileCount: patch.files.length,
        ...(patch.status ? { status: patch.status } : {}),
    }));

    const unresolvedCount = lock.patches.filter(
        (p) => p.status === "unresolved" || p.status === "resolving"
    ).length;

    let lastGeneration: StatusGeneration | undefined;
    const lastGen = lock.generations.find((g) => g.commit_sha === lock.current_generation);
    if (lastGen) {
        lastGeneration = {
            sha: lastGen.commit_sha.slice(0, 7),
            timestamp: lastGen.timestamp,
            cliVersion: lastGen.cli_version,
            generatorVersions: lastGen.generator_versions,
        };
    }

    const config = lockManager.getCustomizationsConfig();
    const excludePatterns = config.exclude ?? [];

    const dismissals = lock.dismissals ?? [];
    const dismissed: StatusDismissal[] = dismissals.map((d) => ({
        message: d.original_message,
        sha: d.original_commit.slice(0, 7),
        via: d.via,
        dismissedAt: d.dismissed_at,
    }));

    // Forgotten hashes recorded before dismissal metadata existed: still
    // operative tombstones, but with nothing to display beyond a count.
    const coveredHashes = new Set(dismissals.flatMap((d) => d.content_hashes));
    const legacyDismissedCount = (lock.forgotten_hashes ?? []).filter((h) => !coveredHashes.has(h)).length;

    return {
        initialized: true,
        generationCount: lock.generations.length,
        lastGeneration,
        patches,
        unresolvedCount,
        excludePatterns,
        dismissed,
        legacyDismissedCount,
    };
}
